import { useEffect, useState } from "react";
import useCache from "../useCache";
import { extractApiPath } from "src/utils/cache/idbUtility";
import { getFromMemory, setToMemory } from "src/utils/cache/memoryCache";

/** Unchanged simple version used by most pages (full cache stack). */
export const useTableCacheForForm = (cacheKey: string, itemKey: string, editId: any) => {
  const [tableData] = useCache<any[]>(cacheKey);
  const arr = Array.isArray(tableData) ? tableData : [];
  return arr.find((item) => item?.[itemKey] == editId);
};

/** Cheap deep clone (no freeze) to avoid mutation bleed. */
export const deepCloneUnsafe = <T,>(v: T): T => {
  try {
    // @ts-ignore
    if (typeof structuredClone === "function") return structuredClone(v);
  } catch {}
  try {
    return JSON.parse(JSON.stringify(v as any));
  } catch {
    if (Array.isArray(v)) return [...(v as any[])] as any;
    if (v && typeof v === "object") return { ...(v as any) };
    return v;
  }
};

/**
 * Sales Item–specific: memory-only read, snapshot set once.
 * - Reads in-memory LRU for the table key.
 * - Finds the matching row by itemKey/editId.
 * - Stores a deep-cloned snapshot ONCE; never updates after that.
 * - No freezing, so editable tables won't blow up.
 */
export const useTableCacheForSalesItem = (
  cacheKey: string,
  itemKey: string,
  editId: any
): { matchedFrozen: any | undefined } => {
  const key = extractApiPath(cacheKey);
  const [snapshot, setSnapshot] = useState<any | undefined>(undefined);

  useEffect(() => {
    // already have a snapshot; do not replace
    if (snapshot) return;

    let mem = getFromMemory<any>(key);

    // If memory exists but is not an array → it is poison from a previous read
    if (mem !== null && mem !== undefined && !Array.isArray(mem)) {
      // Clean it up now so the table never crashes
      setToMemory(key, undefined);
      mem = undefined;
    }
    
    const arr = Array.isArray(mem) ? mem : [];
    const hit = arr.find((item) => item?.[itemKey] == editId);
    if (hit) {
      // clone to break ref to shared table row
      setSnapshot(deepCloneUnsafe(hit));
    }
  }, [key, itemKey, editId, snapshot]);

  return { matchedFrozen: snapshot };
};
